import CONFIG from "../../config/config";

// Define interfaces for type safety
interface PdfOptions {
  [key: string]: any;
}

interface GeneratePdfParams {
  htmlString: string;
  pdfOptions?: PdfOptions;
  s3Key?: string;
  variables?: any[];
}

interface ImageResolution {
  width: number;
  height: number;
}

interface CustomDimensions {
  width: number; // in millimeters
  height: number; // in millimeters
}

interface GenerateFileParams {
  htmlString: string;
  pdfOptions?: PdfOptions;
  s3Key?: string;
  variables?: any[];
  useCMYK?: boolean;
  fileType?: "PNG" | "JPEG" | "PDF_CMYK" | "PDF_RGB";
  quality?: number;
  includeBleedsAndCropMarks?: boolean;
  flattenPDF?: boolean;
  compressionLevel?: "low" | "medium" | "high";
  imageResolution?: ImageResolution;
  useCustomTwoPageLayout?: boolean;
  customDimensions?: CustomDimensions; // New: Custom dimensions in millimeters
  useRowLayout?: boolean;
}

/**
 * Generates a PDF by making a POST request to the puppeteer service.
 * @param {GeneratePdfParams} params - The parameters for PDF generation.
 * @returns {Promise<object>} A promise that resolves with the response data from the service.
 */
export async function generatePdfFromService({
  htmlString,
  pdfOptions,
  s3Key,
  variables,
}: GeneratePdfParams): Promise<any> {
  if (!CONFIG.PUPPETEER_SERVICE.baseUrl) {
    throw new Error("PUPPETEER_SERVICE_URL environment variable is not set. Please define it in your .env file.");
  }

  if (!htmlString) {
    throw new Error("The 'htmlString' parameter is required.");
  }

  const payload = {
    htmlString,
    pdfOptions,
    s3Key,
    variables,
  };

  try {
    const response = await fetch(`${CONFIG.PUPPETEER_SERVICE.baseUrl}/generate-pdf`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("Successfully called Puppeteer service.", data);
    return data;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error calling Puppeteer service:", error.message);
    } else {
      console.error("An unexpected error occurred:", error);
    }
    return "";
  }
}

/**
 * Generates a file (PDF, PNG, JPEG) by making a POST request to the puppeteer service.
 * @param {GenerateFileParams} params - The parameters for file generation.
 * @returns {Promise<object>} A promise that resolves with the response data from the service.
 */
export async function generateFileFromService({
  htmlString,
  pdfOptions = {},
  s3Key,
  variables = [],
  useCMYK = true,
  fileType = "PDF_CMYK",
  quality,
  includeBleedsAndCropMarks,
  flattenPDF,
  compressionLevel,
  imageResolution,
  useCustomTwoPageLayout,
  customDimensions,
  useRowLayout,
}: GenerateFileParams): Promise<any> {
  if (!CONFIG.PUPPETEER_SERVICE.baseUrl) {
    throw new Error("PUPPETEER_SERVICE_URL environment variable is not set. Please define it in your .env file.");
  }

  if (!htmlString) {
    throw new Error("The 'htmlString' parameter is required.");
  }

  // Validate file type
  const validFileTypes = ["PNG", "JPEG", "PDF_CMYK", "PDF_RGB"];
  if (!validFileTypes.includes(fileType)) {
    throw new Error(`Invalid fileType. Must be one of: ${validFileTypes.join(", ")}`);
  }

  // Validate compression level
  const validCompressionLevels = ["low", "medium", "high"];
  if (compressionLevel && !validCompressionLevels.includes(compressionLevel)) {
    throw new Error(`Invalid compressionLevel. Must be one of: ${validCompressionLevels.join(", ")}`);
  }

  const payload = {
    htmlString,
    pdfOptions,
    s3Key,
    variables,
    useCMYK,
    fileType,
    quality,
    includeBleedsAndCropMarks,
    flattenPDF,
    compressionLevel,
    imageResolution,
    useCustomTwoPageLayout,
    customDimensions,
    useRowLayout,
  };

  try {
    const response = await fetch(`${CONFIG.PUPPETEER_SERVICE.baseUrl}/generate-file`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("Successfully called Puppeteer service for file generation.", data);
    return data;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error calling Puppeteer service for file generation:", error.message);
    } else {
      console.error("An unexpected error occurred during file generation:", error);
    }
    return "";
  }
}
